import SearchInput from '../../../components/ui/SearchInput';
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NoData from '../../../components/common/NoData';
import CourseCatalogCard from '../../../components/user/CourseCatalogCard';
import { getMyCourses } from '../../../services/user/course';
import { encodeData } from '../../../utils/encodeAndEncode';
import useCourseCardProgress from '../../../hooks/useCourseCardProgress';
import { getCourseCardState } from '../../../utils/courseCardState';

function MyCourseTab() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const searchTimeout = useRef(null);
  const courseProgress = useCourseCardProgress(courses.filter((course) => !course.is_completed && course.has_current_access).map((course) => course.id));

  const parseClassrooms = (value) => {
    if (Array.isArray(value)) return value;
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  useEffect(() => {
    fetchMyCourses(1, searchTerm, true);
  }, []);

  useEffect(() => {
    const refreshCourses = () => fetchMyCourses(1, searchTerm, true);
    window.addEventListener('focus', refreshCourses);
    return () => window.removeEventListener('focus', refreshCourses);
  }, [searchTerm]);

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 100 >= document.documentElement.offsetHeight &&
        hasMore && !isLoading
      ) {
        fetchMyCourses(page + 1, searchTerm);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [page, hasMore, isLoading]);

  const fetchMyCourses = async (pageNumber = 1, search = '', reset = false) => {
    try {
      setIsLoading(true);
      if (pageNumber === 1) setLoadError(false);
      const response = await getMyCourses({ page: pageNumber, limit: 10, search });

      if (response.success && response.data?.courses.length > 0) {
        const mapped = response.data.courses.map((course, index) => ({
          id: course.course_id,
          title: course.course_name,
          description: course.course_desc,
          image: course.banner_image,
          modules: response.data.metadata?.[index]?.total_modules || 0,
          enrolled: response.data.metadata?.[index]?.totalUsers || 0,
          rating: course.total_rating || 0,
          level: course.levels || 'N/A',
          category: Array.isArray(course.course_categories)
            ? course.course_categories.join(', ')
            : course.course_categories || '',
          is_public: Boolean(course.is_public),
          access_scope: course.access_scope || 'catalogue',
          classrooms: parseClassrooms(course.classrooms),
          has_current_access: Number(course.has_current_access) === 1,
          price: String(course.course_type || '').toLowerCase() === 'free' ? 'Free' : course.course_price,
          course_type: course.course_type,
          currency: course.currency,
          organization_name: course.organization_name || '',
          is_completed: Number(course.is_completed) === 1,
        })).filter((course) => course.access_scope !== 'classroom_only' || course.has_current_access);
        setCourses(prev => reset ? mapped : [...prev, ...mapped]);
        setPage(pageNumber);
        setHasMore(pageNumber < response.data.totalPages);
      } else {
        if (reset) setCourses([]);
        setHasMore(false);
      }
    } catch (error) {
      if (pageNumber === 1) setLoadError(true);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchMyCourses(1, value, true);
    }, 500);
  };

  const handleCourseClick = (courseId) => {
    const encoded = encodeData({ id: courseId });
    navigate(`/user/courses/WatchCourse/${encodeURIComponent(encoded)}`);
  };

  const openClassroom = (classroom) => {
    if (!classroom?.id) return;
    const encodedClassroomId = encodeData(classroom.id);
    navigate(`/user/AllClassroom/classroomDetails/${encodeURIComponent(encodedClassroomId)}?active-tab=courses`);
  };

  return (
    <div className="course-tab-content">
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-4 tw-mt-2"}>
        <div className="tw-min-w-[0px] ui-column md:tw-w-1/3">
          <div>
            <SearchInput
              type="text"
              className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input"}
              placeholder="Search courses..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
        </div>
      </div>

      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row"}>
        {isLoading && courses.length === 0 && (
          <div className="tw-flex tw-justify-center tw-items-center tw-w-full" style={{ minHeight: '220px' }}>
            <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
              <span className="tw-sr-only">Loading...</span>
            </div>
          </div>
        )}

        {loadError && courses.length === 0 && !isLoading ? (
          <div className="tw-w-full">
            <NoData
              message="Your courses are temporarily unavailable"
              description="Please try again in a moment."
              icon="mdi:cloud-alert-outline"
              onRetry={() => fetchMyCourses(1, searchTerm, true)}
            />
          </div>
        ) : courses.length === 0 && !isLoading ? (
          <div className="tw-flex tw-justify-center tw-items-center tw-w-full" style={{ minHeight: '300px' }}>
            <NoData message="No courses found." />
          </div>
        ) : (
          courses.map(course => {
            const cardState = getCourseCardState(course, 'ready', courseProgress[String(course.id)]);
            if (!course.has_current_access) {
              cardState.progress = null;
              cardState.progressLoading = false;
            }

            return (
              <div key={course.id} className="max-[639px]:!tw-w-full min-[640px]:max-[1023px]:!tw-w-1/2 tw-min-w-[0px] ui-column lg:tw-w-1/4 tw-mb-2">
                <CourseCatalogCard
                  course={course}
                  onPrimaryAction={(selectedCourse) => handleCourseClick(selectedCourse.id)}
                  {...cardState}
                  disablePrimaryAction={!course.has_current_access}
                  onClassroomAction={openClassroom}
                  compact
                />
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default MyCourseTab;
